import BaseService from '@/services/BaseService';
/** Asset-class scope keys mapped to Digitalconnect API path prefixes. */
export type FacilityPerformanceScope =
  | 'auto'
  | 'aviation'
  | 'marine'
  | 'railcar'
  | 'sme'
  | 'non-us-cre'
  | 'non-us-resi'
  | 'fund-finance'
  | 'private-credit-finance'
  | 'triple-net-lease'
  | 'student-loan';

export const FACILITY_PERFORMANCE_SCOPE_PREFIX: Record<FacilityPerformanceScope, string> = {
  auto: 'facility-performance',
  aviation: 'aviation-facility-performance',
  marine: 'marine-facility-performance',
  railcar: 'railcar-facility-performance',
  sme: 'sme-facility-performance',
  'non-us-cre': 'non-us-cre-facility-performance',
  'non-us-resi': 'non-us-resi-facility-performance',
  'fund-finance': 'fund-finance-facility-performance',
  'private-credit-finance': 'private-credit-finance-facility-performance',
  'triple-net-lease': 'triple-net-lease-facility-performance',
  'student-loan': 'student-loan-facility-performance',
};

export type FacilityPerformanceFacilityOption = {
  facilityId: string;
  facilityName: string;
};

export type FacilityPerformanceFacilitiesFilter = {
  assetClass?: string;
  consumerUnsecuredSubAssetClass?: 'Non-Marketplace' | 'Marketplace';
};

export type FacilityPerformanceMeasureNameQueryBody = {
  facilityId: string;
  measureCategory: string;
  measureName: string;
};

export type FacilityPerformanceRawTriggerQueryBody = {
  facilityId: string;
  measureCategory: string;
  rawTriggerName: string;
};

export type FacilityPerformanceQueryBody =
  | FacilityPerformanceMeasureNameQueryBody
  | FacilityPerformanceRawTriggerQueryBody;

export type FacilityPerformanceThresholdRow = {
  reportDate: string | null;
  reportYear: number;
  reportMonth: number;
  threshold: string | null;
  value: string | null;
  thresholdLabel?: string | null;
  additionalInfo?: string | null;
};

export type FacilityPerformanceAutoRow = {
  periodEnd: string | null;
  reportYear: number;
  reportMonth: number;
  arStepdown: string | null;
  eaeThreshold: string | null;
  eodThreshold: string | null;
  levelI: string | null;
  levelII: string | null;
  currentValue: string | null;
};

export type FacilityPerformanceQueryResult<T> = { data: T[] };

function scopePrefix(scope: FacilityPerformanceScope): string {
  return FACILITY_PERFORMANCE_SCOPE_PREFIX[scope];
}

function measureNamesSegment(scope: FacilityPerformanceScope): 'measure-names' | 'trigger-names' {
  return scope === 'auto' ? 'trigger-names' : 'measure-names';
}

/** HTTP client for facility-performance endpoints across asset-class scopes. */
export class FacilityPerformanceService extends BaseService {
  constructor() {
    super('/api/digitalconnect-service');
  }

  fetchFacilities = async (
    scope: FacilityPerformanceScope,
    filters?: FacilityPerformanceFacilitiesFilter,
  ): Promise<FacilityPerformanceFacilityOption[]> => {
    const params: Record<string, string> = {};
    if (filters?.assetClass) {
      params.assetClass = filters.assetClass;
    }
    if (filters?.consumerUnsecuredSubAssetClass) {
      params.consumerUnsecuredSubAssetClass = filters.consumerUnsecuredSubAssetClass;
    }
    const path = `${scopePrefix(scope)}/facilities`;
    const response =
      Object.keys(params).length > 0 ? await this.get({ path: path, params: params }) : await this.get({ path: path });
    if (!Array.isArray(response)) return [];
    if (scope === 'student-loan') {
      return response.filter(
        (facility: FacilityPerformanceFacilityOption) =>
          !(facility.facilityId ?? '').toUpperCase().startsWith('SP_COVENANTS_AND_TRIGGERS_') &&
          !(facility.facilityName ?? '').toUpperCase().startsWith('SP_COVENANTS_AND_TRIGGERS_'),
      );
    }
    return response;
  };

  fetchMeasureCategories = async (scope: FacilityPerformanceScope, facilityId: string): Promise<string[]> => {
    const path = `${scopePrefix(scope)}/measure-categories`;
    const response = await this.get({ path: path, params: { facilityId } });
    return Array.isArray(response) ? response : [];
  };

  fetchMeasureNames = async (
    scope: FacilityPerformanceScope,
    facilityId: string,
    measureCategory: string,
  ): Promise<string[]> => {
    const path = `${scopePrefix(scope)}/${measureNamesSegment(scope)}`;
    const response = await this.get({ path: path, params: { facilityId, measureCategory } });
    const names = Array.isArray(response) ? response : [];
    return names.filter((name): name is string => typeof name === 'string' && name.trim() !== '');
  };

  fetchPerformanceQuery = async <TRow>(
    scope: FacilityPerformanceScope,
    body: FacilityPerformanceQueryBody,
  ): Promise<FacilityPerformanceQueryResult<TRow>> => {
    const path = `${scopePrefix(scope)}/query`;
    const result = await this.post<FacilityPerformanceQueryBody, FacilityPerformanceQueryResult<TRow>>({
      path,
      body,
    });
    return { data: result?.data ?? [] };
  };
}
